import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Notification {
  id: string;
  user_id: string | null;
  title: string;
  message: string;
  type: string;
  link: string | null;
  created_at: string;
  created_by: string | null;
  is_read: boolean;
}

// Cross-instance sync: every hook instance listens for changes triggered
// by any other instance (bell + notifications page must stay in sync).
type SyncEvent =
  | { kind: "read"; ids: string[] }
  | { kind: "dismiss"; ids: string[] };
const syncListeners = new Set<(e: SyncEvent) => void>();
const emitSync = (e: SyncEvent) => syncListeners.forEach((l) => l(e));

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    // Fetch notifications (RLS handles filtering to own + broadcasts)
    const { data: notifs, error: notifError } = await supabase
      .from("notifications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);

    if (notifError) {
      console.error("Error fetching notifications:", notifError);
      setLoading(false);
      return;
    }

    // Fetch read status
    const { data: reads } = await supabase
      .from("notification_reads")
      .select("notification_id");

    const readIds = new Set((reads ?? []).map((r: { notification_id: string }) => r.notification_id));

    // Fetch dismissals so the user can hide notifications they no longer
    // want to see. Dismissed items are filtered out entirely.
    const { data: dismissals } = await supabase
      .from("notification_dismissals")
      .select("notification_id");
    const dismissedIds = new Set(
      (dismissals ?? []).map((d: { notification_id: string }) => d.notification_id),
    );

    // Broadcasts (user_id IS NULL) should NOT include announcements posted
    // before this user signed up — they aren't "news" to a brand-new user.
    // Per-user notifications (user_id === auth.uid) are always shown.
    const signupTime = user.created_at ? new Date(user.created_at).getTime() : 0;
    const mapped: Notification[] = (notifs ?? [])
      .filter((n: any) => {
        if (dismissedIds.has(n.id)) return false;
        if (n.user_id) return true; // directed at this user
        const created = new Date(n.created_at).getTime();
        return created >= signupTime;
      })
      .map((n: any) => ({
      id: n.id,
      user_id: n.user_id,
      title: n.title,
      message: n.message,
      type: n.type,
      link: n.link,
      created_at: n.created_at,
      created_by: n.created_by,
      is_read: readIds.has(n.id),
    }));

    setNotifications(mapped);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Listen for read/dismiss events triggered by other hook instances
  // so the bell badge and notifications page never drift out of sync.
  useEffect(() => {
    const listener = (e: SyncEvent) => {
      if (e.kind === "read") {
        setNotifications((prev) =>
          prev.map((n) => (e.ids.includes(n.id) ? { ...n, is_read: true } : n)),
        );
      } else if (e.kind === "dismiss") {
        setNotifications((prev) => prev.filter((n) => !e.ids.includes(n.id)));
      }
    };
    syncListeners.add(listener);
    return () => {
      syncListeners.delete(listener);
    };
  }, []);

  // Realtime subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        (payload) => {
          const n = payload.new as any;
          // Add per-user notifications, OR broadcasts posted after the user
          // signed up. Older broadcasts are not surfaced retroactively.
          const signupTime = user.created_at ? new Date(user.created_at).getTime() : 0;
          const isOwn = n.user_id === user.id;
          const isFreshBroadcast =
            n.user_id === null &&
            new Date(n.created_at).getTime() >= signupTime;
          if (isOwn || isFreshBroadcast) {
            setNotifications((prev) => [
              {
                id: n.id,
                user_id: n.user_id,
                title: n.title,
                message: n.message,
                type: n.type,
                link: n.link,
                created_at: n.created_at,
                created_by: n.created_by,
                is_read: false,
              },
              ...prev,
            ]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!user) return;
      await supabase
        .from("notification_reads")
        .insert({ notification_id: notificationId, user_id: user.id });

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      emitSync({ kind: "read", ids: [notificationId] });
    },
    [user]
  );

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    const unread = notifications.filter((n) => !n.is_read);
    if (unread.length === 0) return;

    const inserts = unread.map((n) => ({
      notification_id: n.id,
      user_id: user.id,
    }));

    await supabase.from("notification_reads").insert(inserts);

    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    emitSync({ kind: "read", ids: unread.map((n) => n.id) });
  }, [user, notifications]);

  // Dismiss a single notification for this user. Inserts a row in
  // notification_dismissals so the item stays hidden across reloads and
  // realtime updates, then removes it optimistically from local state.
  const dismiss = useCallback(
    async (notificationId: string) => {
      if (!user) return;
      await supabase
        .from("notification_dismissals")
        .insert({ notification_id: notificationId, user_id: user.id });
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      emitSync({ kind: "dismiss", ids: [notificationId] });
    },
    [user],
  );

  // Clear (dismiss) all currently visible notifications.
  const clearAll = useCallback(async () => {
    if (!user || notifications.length === 0) return;
    const inserts = notifications.map((n) => ({
      notification_id: n.id,
      user_id: user.id,
    }));
    await supabase.from("notification_dismissals").insert(inserts);
    setNotifications([]);
    emitSync({ kind: "dismiss", ids: notifications.map((n) => n.id) });
  }, [user, notifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    dismiss,
    clearAll,
    refetch: fetchNotifications,
  };
}
